import { EventCard } from '../event-card';
import { MarketCard, MarketCardSkeleton } from '../market-card';
import { dropTruncatedEventLegs, groupMarketsByEvent } from '../market-list/event-groups';
import { useTrendingMarkets } from '@/client/hooks/use-trending-markets';
import { useIsShortRoundMarket } from '@/client/pages/btc-5m/use-rounds-feed';
import { getScrollBehavior } from '@/client/utils/dom';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "Trending now" section (design handoff, ported from AIP) — top markets by
 * volume in a 3-up grid above the browse list. Uses the existing markets
 * endpoint (volume sort).
 */
export const TrendingNow = () => {
  const { t } = useTranslation(['home']);
  const { data, isPending } = useTrendingMarkets();

  // Collapse multi-market events into one card, drop the legs the volume-ranked
  // page cut off from their siblings, then take the top 3. Grouping alone does
  // not stop a lone leg from surfacing as a context-free card -- only some of an
  // event's legs need land in the page for that to happen.
  // 5-minute round events are left out: the home series card stands for them.
  const isShortRound = useIsShortRoundMarket();
  const top = useMemo(
    () =>
      dropTruncatedEventLegs(
        groupMarketsByEvent((data?.pages.flatMap(p => p.markets) ?? []).filter(m => !isShortRound(m)))
      ).slice(0, 3),
    [data, isShortRound]
  );

  if (!isPending && top.length === 0) return null;

  return (
    <section className="px-4 md:px-6 pt-6 pb-2">
      <div className="neon-sechead">
        <div>
          <h2>{t('trending-now', 'Trending now')}</h2>
          <p>{t('trending-now-sub', 'Markets moving fast in the last 24 hours.')}</p>
        </div>
        <span
          className="neon-viewall"
          // DPIN has no standalone /markets route (unlike AIP) -- "View all"
          // scrolls to the existing browse list instead of a dead link.
          onClick={() =>
            document.querySelector('#market-list')?.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' })
          }
        >
          {t('view-all', 'View all')} →
        </span>
      </div>
      <div className="neon-grid-3">
        {isPending && Array.from({ length: 3 }).map((_, i) => <MarketCardSkeleton key={i} />)}
        {top.map((g, i) =>
          g.markets.length > 1 ? (
            <EventCard key={`ev-${g.eventId}`} eventId={g.eventId} markets={g.markets} />
          ) : (
            <MarketCard key={g.markets[0].marketAddress} market={g.markets[0]} index={i} />
          )
        )}
      </div>
    </section>
  );
};
